import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment, { Moment } from 'moment';
import { getComparisonDateTime } from 'utils/dashboard';
import QUERY from 'utils/query';

type DefaultRange = 'today' | '7days' | '30days';

interface UseDateRangeOptions {
  /** Whether to sync dates to URL. Default: false */
  syncToUrl?: boolean;
  /** Default date range if no dates in URL. Default: 'today' */
  defaultRange?: DefaultRange;
  /** URL parameter key for dates. Default: 'dates' */
  urlParamKey?: string;
}

interface UseDateRangeReturn {
  dateFrom: Moment;
  dateTo: Moment;
  setDateFrom: React.Dispatch<React.SetStateAction<Moment>>;
  setDateTo: React.Dispatch<React.SetStateAction<Moment>>;
  comparisonFrom: string;
  comparisonTo: string;
  /** ISO string of dateFrom */
  dateFromISO: string;
  /** ISO string of dateTo */
  dateToISO: string;
}

const getDefaultDateRange = (range: DefaultRange): [Moment, Moment] => {
  const now = moment();
  switch (range) {
    case '7days':
      return [moment().subtract(7, 'days').startOf('day'), now.endOf('day')];
    case '30days':
      return [moment().subtract(30, 'days').startOf('day'), now.endOf('day')];
    case 'today':
    default:
      return [moment().startOf('day'), moment().endOf('day')];
  }
};

/**
 * Custom hook for managing date range state with optional URL synchronization.
 * Handles date parsing from URL, comparison date calculation, and URL updates.
 */
const useDateRange = (
  options: UseDateRangeOptions = {}
): UseDateRangeReturn => {
  const {
    syncToUrl = false,
    defaultRange = 'today',
    urlParamKey = 'dates',
  } = options;

  const location = useLocation();
  const navigate = useNavigate();

  // Parse dates from URL if syncing
  const getInitialDates = useCallback((): [Moment, Moment] => {
    if (!syncToUrl) {
      return getDefaultDateRange(defaultRange);
    }

    const urlParams = new URLSearchParams(location.search);
    const datesParam = urlParams.get(urlParamKey);

    if (datesParam) {
      const dates = datesParam.split(',');
      if (dates.length >= 2) {
        // Handle timezone encoding - replace spaces with + for proper parsing
        const urlDateFrom = dates[0].replaceAll(' ', '+');
        const urlDateTo = dates[1].replaceAll(' ', '+');

        const parsedFrom = moment(urlDateFrom);
        const parsedTo = moment(urlDateTo);

        if (parsedFrom.isValid() && parsedTo.isValid()) {
          return [parsedFrom, parsedTo];
        }
      }
    }

    return getDefaultDateRange(defaultRange);
  }, [syncToUrl, defaultRange, urlParamKey, location.search]);

  const [initialFrom, initialTo] = getInitialDates();
  const [dateFrom, setDateFromState] = useState<Moment>(initialFrom);
  const [dateTo, setDateToState] = useState<Moment>(initialTo);

  // Memoized ISO strings
  const dateFromISO = useMemo(() => dateFrom.toISOString(), [dateFrom]);
  const dateToISO = useMemo(() => dateTo.toISOString(), [dateTo]);

  // Calculate comparison dates
  const [comparisonFrom, comparisonTo] = useMemo(() => {
    const comparisonDates = getComparisonDateTime(dateFromISO, dateToISO);

    if (comparisonDates && comparisonDates[0] && comparisonDates[1]) {
      return comparisonDates;
    }

    // Fallback: calculate manually if getComparisonDateTime fails
    const from = moment(dateFromISO);
    const to = moment(dateToISO);
    const intervalDays = to.diff(from, 'days');

    return [
      from
        .clone()
        .subtract(intervalDays + 1, 'days')
        .toISOString(),
      to
        .clone()
        .subtract(intervalDays + 1, 'days')
        .toISOString(),
    ];
  }, [dateFromISO, dateToISO]);

  // Sync dates to URL when they change
  useEffect(() => {
    if (!syncToUrl) return;

    const urlParams = new URLSearchParams(location.search);
    const currentUrlDates = urlParams.get(urlParamKey);
    const newDates = `${dateFromISO},${dateToISO}`;

    // Only update URL if dates have changed
    if (currentUrlDates !== newDates) {
      navigate(
        {
          search: QUERY.update(location.search, {
            [urlParamKey]: newDates,
          }),
        },
        { replace: true }
      );
    }
  }, [
    dateFromISO,
    dateToISO,
    syncToUrl,
    urlParamKey,
    navigate,
    location.search,
  ]);

  return {
    dateFrom,
    dateTo,
    setDateFrom: setDateFromState,
    setDateTo: setDateToState,
    comparisonFrom,
    comparisonTo,
    dateFromISO,
    dateToISO,
  };
};

export default useDateRange;
